import * as React from "react";
import type { VariantProps } from "class-variance-authority";
import type { Table as TanStackTable, RowData } from "@tanstack/react-table";
import { cn } from "../../lib/utils";
import { TableHeader } from "./TableHeader";
import { TableBody } from "./TableBody";
import { tableVariants, tableCellVariants } from "./tableVariants";
import {
  renderDefaultLoadingState,
  renderDefaultEmptyState,
} from "./tableHelpers";
import { Skeleton } from "../Skeleton";

// Extend TanStack Table types to support custom meta properties
declare module "@tanstack/react-table" {
  interface ColumnMeta<TData extends RowData, TValue> {
    headerClassName?: string;
    cellClassName?: string;
  }
}

// ==================== Types ====================

export interface TableProps<TData extends RowData>
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof tableVariants> {
  table: TanStackTable<TData>;
  enableRowSelection?: boolean;
  enableSelectAll?: boolean;
  isLoading?: boolean;
  loadingComponent?: React.ReactNode;
  emptyComponent?: React.ReactNode;
  wrapperClassName?: string;
  containerClassName?: string;
  enableHorizontalScroll?: boolean;
  stickyHeader?: boolean;
  showRowHover?: boolean;
  onRowClick?: (row: TData) => void;
  rowClassName?: string | ((row: TData) => string);
  headerClassName?: string;
  cellClassName?: string;
  showHeaderBackground?: boolean;
  loading?: boolean;
  loadingSkeletonRows?: number;
  loadingSkeletonHeight?: number | string;
  columnWidths?: Partial<Record<string, string | number>>;
  columnMinWidths?: Partial<Record<string, string | number>>;
  columnMaxWidths?: Partial<Record<string, string | number>>;
  size?: "small" | "medium" | "large" | "auto";
}

// ==================== Component ====================

function TableComponent<TData extends RowData>(
  {
    className,
    wrapperClassName,
    containerClassName,
    variant,
    size = "medium",
    table,
    enableRowSelection = false,
    enableSelectAll = false,
    isLoading = false,
    loading,
    loadingComponent,
    emptyComponent,
    enableHorizontalScroll = false,
    stickyHeader = false,
    showRowHover = true,
    onRowClick,
    rowClassName,
    headerClassName,
    cellClassName,
    showHeaderBackground = true,
    loadingSkeletonRows,
    loadingSkeletonHeight,
    columnWidths,
    columnMinWidths,
    columnMaxWidths,
    ...props
  }: TableProps<TData>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  // ==================== Computed Values ====================

  const hasData = table.getRowModel().rows?.length > 0;
  const headerGroups = table.getHeaderGroups();

  const normalizeSizeValue = (value?: string | number) => {
    if (value === undefined) return undefined;
    return typeof value === "number" ? `${value}px` : value;
  };

  const getColumnStyle = React.useCallback(
    (
      columnId: string,
      fallback?: {
        width?: number;
        minWidth?: number;
        maxWidth?: number;
      }
    ): React.CSSProperties => {
      const width = columnWidths?.[columnId] ?? fallback?.width ?? undefined;
      const minWidth =
        columnMinWidths?.[columnId] ?? fallback?.minWidth ?? undefined;
      const maxWidth =
        columnMaxWidths?.[columnId] ?? fallback?.maxWidth ?? undefined;

      return {
        width: normalizeSizeValue(width),
        minWidth: normalizeSizeValue(minWidth),
        maxWidth: normalizeSizeValue(maxWidth),
      };
    },
    [columnWidths, columnMinWidths, columnMaxWidths]
  );

  // Resolve loading state: prefer 'loading' prop, fallback to 'isLoading' for backward compatibility
  const resolvedLoading =
    typeof loading === "boolean" ? loading : Boolean(isLoading);

  const skeletonRowCount = loadingSkeletonRows ?? 5;

  const sizeKey = size || "medium";

  const skeletonHeightMap: Record<
    NonNullable<typeof sizeKey>,
    number | string
  > = {
    small: 16,
    medium: 20,
    large: 24,
    auto: "auto",
  };

  const skeletonCellHeight = normalizeSizeValue(
    loadingSkeletonHeight ?? skeletonHeightMap[sizeKey]
  );

  const selectionSkeletonSize =
    sizeKey === "small" ? 14 : sizeKey === "large" ? 20 : 16;

  const renderSkeletonRows = React.useCallback(() => {
    const visibleColumns = table.getVisibleLeafColumns();

    if (visibleColumns.length === 0) {
      return renderDefaultLoadingState({
        colSpan: table.getAllColumns().length || 1,
      });
    }

    return Array.from({ length: skeletonRowCount }).map((_, rowIndex) => (
      <tr key={`skeleton-row-${rowIndex}`}>
        {enableRowSelection && (
          <td
            className={cn(
              tableCellVariants({ size: sizeKey }),
              "w-10",
              cellClassName
            )}
          >
            <Skeleton
              variant="circle"
              width={selectionSkeletonSize}
              height={selectionSkeletonSize}
            />
          </td>
        )}
        {visibleColumns.map((column) => (
          <td
            key={`${column.id}-${rowIndex}`}
            className={cn(
              tableCellVariants({ size: sizeKey }),
              column.columnDef.meta?.cellClassName,
              cellClassName
            )}
            style={getColumnStyle(column.id, {
              width: column.getSize(),
              minWidth: column.columnDef.minSize,
              maxWidth: column.columnDef.maxSize,
            })}
          >
            <Skeleton
              className="w-full"
              height={skeletonCellHeight}
              rounded="medium"
            />
          </td>
        ))}
      </tr>
    ));
  }, [
    cellClassName,
    enableRowSelection,
    getColumnStyle,
    selectionSkeletonSize,
    sizeKey,
    skeletonCellHeight,
    skeletonRowCount,
    table,
  ]);

  const renderLoadingContent = React.useCallback(() => {
    if (loadingComponent) return loadingComponent;
    return renderSkeletonRows();
  }, [loadingComponent, renderSkeletonRows]);

  // ==================== Callbacks ====================

  const getRowClassName = React.useCallback(
    (row: TData): string => {
      if (typeof rowClassName === "function") {
        return rowClassName(row);
      }
      return rowClassName || "";
    },
    [rowClassName]
  );

  const handleRowClickInternal = React.useCallback(
    (row: TData) => {
      if (onRowClick) {
        onRowClick(row);
      }
    },
    [onRowClick]
  );

  // ==================== Render Helpers ====================

  const renderEmptyState = () => {
    if (emptyComponent) return emptyComponent;
    return renderDefaultEmptyState({ colSpan: table.getAllColumns().length });
  };

  // ==================== Render ====================

  return (
    <div ref={ref} className={cn("w-full", wrapperClassName)} {...props}>
      <div
        className={cn(
          "relative",
          enableHorizontalScroll ? "overflow-x-auto" : "overflow-x-hidden",
          containerClassName
        )}
      >
        <table className={cn(tableVariants({ variant, size }), className)}>
          <TableHeader
            headerGroups={headerGroups}
            enableRowSelection={enableRowSelection}
            enableSelectAll={enableSelectAll}
            showHeaderBackground={showHeaderBackground}
            stickyHeader={stickyHeader}
            size={size as "small" | "medium" | "large" | "auto"}
            headerClassName={headerClassName}
            onToggleAllRows={(e) => table.getToggleAllRowsSelectedHandler()(e)}
            isAllRowsSelected={table.getIsAllRowsSelected()}
            isSomeRowsSelected={table.getIsSomeRowsSelected()}
            getColumnStyle={getColumnStyle}
          />

          {resolvedLoading ? (
            <tbody>{renderLoadingContent()}</tbody>
          ) : !hasData ? (
            <tbody>{renderEmptyState()}</tbody>
          ) : (
            <TableBody
              rows={table.getRowModel().rows}
              enableRowSelection={enableRowSelection}
              size={size || "medium"}
              variant={variant || "default"}
              showRowHover={showRowHover}
              cellClassName={cellClassName}
              onRowClick={onRowClick}
              getRowClassName={getRowClassName}
              handleRowClick={handleRowClickInternal}
              getColumnStyle={getColumnStyle}
            />
          )}
        </table>
      </div>
    </div>
  );
}

// ==================== Export ====================

export const Table = React.forwardRef(TableComponent) as <
  TData extends RowData
>(
  props: TableProps<TData> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => ReturnType<typeof TableComponent>;

(Table as any).displayName = "Table";

export * from "./tableVariants";
